/**
 * Drivers down the side, days across the top.
 *
 * Weekend cover, the driver who quietly stopped turning up three weeks ago, and the week
 * everybody worked — none of which is visible in a table of totals, and all of which is
 * obvious here.
 *
 * A table of shaded cells rather than a charting library, for the same reason the map is
 * lazily loaded: this console is a workstation somebody opens at six in the morning on
 * office hardware, and a heatmap is about forty lines of CSS.
 *
 * It stays an HTML table rather than becoming a grid of divs. A month of a whole fleet is a
 * few thousand cells, and a table is the one structure that gives a screen reader the
 * driver's name and the date for each of them without repeating either into every cell.
 * `DutyGrid.css` beside this file holds the four accessibility choices and says which they
 * are.
 */

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { useTenantFormats } from '../shell/formats';
import { Card, EmptyState } from '../ui';
import type { DutyGrid } from './api';

import './DutyGrid.css';

export function DutyGridView({
  grid,
  onOpenDriver,
}: {
  grid: DutyGrid;
  onOpenDriver: (driverId: string, date: string) => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const days = daysBetween(grid.from, grid.to);

  // The busiest day anybody worked, so the shading uses the whole range rather than
  // squeezing every ordinary day into the bottom two shades.
  const longest = Math.max(
    60,
    ...grid.drivers.flatMap((driver) => driver.days.map((day) => day.minutes)),
  );

  return (
    <>
      <Card>
        {grid.drivers.length === 0 ? (
          <EmptyState>{t('reports.shiftBook.nobodyWorked')}</EmptyState>
        ) : (
          // A month is wider than any column this page has. It scrolls inside the card, so
          // the period row and the header stay where somebody left them. The padding is on
          // the outside of the scrolling box rather than on it: a sticky column pins itself
          // to the scrollport's padding box, so padding the scroller itself would let the
          // pinned names slide left into the card's border as soon as the grid moved.
          <div className="px-3.5 pt-6 pb-3.5">
            <div className="overflow-x-auto">
              <table className="duty-grid">
                <thead>
                  <tr>
                    <th scope="col" className="duty-pin">
                      {t('reports.shiftBook.driver')}
                    </th>
                    {days.map((day) => (
                      <th key={day} scope="col">
                        <span className="duty-day">{formats.day(day)}</span>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {grid.drivers.map((driver) => {
                    const worked = new Map(driver.days.map((day) => [day.date, day]));

                    return (
                      <tr key={driver.driverId}>
                        <th scope="row" className="duty-pin">
                          {driver.displayName}
                          {!driver.isActive && (
                            <small className="text-ink-3 font-normal">
                              {' '}
                              · {t('reports.shiftBook.left')}
                            </small>
                          )}
                        </th>

                        {days.map((date) => {
                          const cell = worked.get(date);

                          if (cell === undefined) {
                            return (
                              <td key={date} className="duty-cell duty-empty">
                                <span className="sr-only">
                                  {t('reports.grid.notWorked', { day: formats.dayLong(date) })}
                                </span>
                              </td>
                            );
                          }

                          return (
                            <td
                              key={date}
                              className={cell.chargeable ? 'duty-cell' : 'duty-cell duty-below'}
                              style={
                                { '--shade': shade(cell.minutes, longest) } as CSSProperties
                              }
                            >
                              <button
                                type="button"
                                onClick={() => onOpenDriver(driver.driverId, date)}
                                title={t('reports.grid.cell', {
                                  driver: driver.displayName,
                                  day: formats.dayLong(date),
                                  hours: formats.hours(cell.minutes),
                                })}
                              >
                                <span className="sr-only">
                                  {t('reports.grid.worked', {
                                    day: formats.dayLong(date),
                                    hours: formats.hours(cell.minutes),
                                  })}
                                </span>
                                <span aria-hidden="true">{Math.round(cell.minutes / 60)}</span>
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

      <p className="text-secondary text-ink-3 m-0">
        {t('reports.grid.legend', { minutes: grid.minimumMinutes })}
      </p>
    </>
  );
}

/** How dark a cell is, between a fifth and full. Never zero: a worked day is never blank. */
function shade(minutes: number, longest: number): number {
  return Math.round((0.2 + 0.8 * Math.min(1, minutes / longest)) * 100) / 100;
}

/** Every local day in the period, as `yyyy-MM-dd`. */
function daysBetween(from: string, to: string): string[] {
  const days: string[] = [];

  // Stepped in UTC over date-only values, so a clock change never adds or drops a column.
  for (
    let day = new Date(`${from}T00:00:00Z`);
    day <= new Date(`${to}T00:00:00Z`);
    day.setUTCDate(day.getUTCDate() + 1)
  ) {
    days.push(day.toISOString().slice(0, 10));
  }

  return days;
}
